import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import unidadesMedidaAPI from '../services/unidadesMedidaAPI';
import {
  UnidadMedida,
  CreateUnidadMedidaRequest,
  UpdateUnidadMedidaRequest,
  UnidadMedidaFilters,
} from '../types/unidadesMedida';

const QUERY_KEY = 'unidades-medida';

/**
 * Hook para obtener todas las unidades de medida
 */
export const useUnidadesMedida = (filters?: UnidadMedidaFilters) => {
  return useQuery({
    queryKey: [QUERY_KEY, filters],
    queryFn: () => unidadesMedidaAPI.getAll(filters),
    staleTime: 10 * 60 * 1000, // 10 minutos (datos casi estáticos)
  });
};

/**
 * Hook para obtener solo unidades activas
 */
export const useUnidadesMedidaActivas = () => {
  return useQuery({
    queryKey: [QUERY_KEY, { activo: true }],
    queryFn: () => unidadesMedidaAPI.getActivas(),
    staleTime: 10 * 60 * 1000,
  });
};

/**
 * Hook para obtener una unidad de medida por ID
 */
export const useUnidadMedida = (id: number) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: () => unidadesMedidaAPI.getById(id),
    enabled: !!id,
  });
};

/**
 * Hook para crear una nueva unidad de medida
 */
export const useCreateUnidadMedida = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUnidadMedidaRequest) => unidadesMedidaAPI.create(data),
    onSuccess: (nuevaUnidad) => {
      // Actualizar cache optimísticamente
      queryClient.setQueryData<UnidadMedida[]>([QUERY_KEY], (old = []) => {
        return [...old, nuevaUnidad];
      });

      // Actualizar cache de unidades activas si corresponde
      if (nuevaUnidad.activo) {
        queryClient.setQueryData<UnidadMedida[]>([QUERY_KEY, { activo: true }], (old = []) => {
          return [...old, nuevaUnidad];
        });
      }

      // Invalidar todas las queries relacionadas para refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

/**
 * Hook para actualizar una unidad de medida existente
 */
export const useUpdateUnidadMedida = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUnidadMedidaRequest }) =>
      unidadesMedidaAPI.update(id, data),
    onSuccess: (unidadActualizada) => {
      // Actualizar cache de la unidad específica
      queryClient.setQueryData([QUERY_KEY, unidadActualizada.id], unidadActualizada);

      // Invalidar todas las queries para refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

/**
 * Hook para desactivar una unidad de medida
 */
export const useDeleteUnidadMedida = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => unidadesMedidaAPI.delete(id),
    onSuccess: () => {
      // Invalidar todas las queries para refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
